<template>
    <div>
        <v-navigation-drawer
            permanent
            app
            :expand-on-hover="mini"
            :mini-variant="mini"
            class="kp-toolbar-bg navdrawer kp-navdrawer"
            width="var(--vuetify-application-left)"
        >
            <div class="navheader d-flex" style="height: 45px">
                <a :href="frontendUrl" target="_blank">
                    <v-img
                        v-if="!mini"
                        contain
                        class="logo"
                        :src="logo.image"
                        :alt="logo.alt"
                        transition="scale-transition"
                    />
                </a>
                <v-spacer v-if="!mini"></v-spacer>
                <v-btn icon class="mt-1 ml-1" @click.stop="mini = !mini">
                    <KpIcon size="20" :icon-cls="`${mini ? 'next' : 'prev'}`" :color="mainNavDrawerCollapseIconColor" />
                </v-btn>
            </div>

            <v-divider />

            <v-list dense nav class>
                <v-list-item link :to="{ name: 'home' }" exact class="nav-entry">
                    <KpIcon size="20" icon-cls="dashboard" color="grey" active-color="white" left />

                    <v-list-item-content>
                        <v-list-item-title>{{ $t('dashboard') }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                    </v-list-item-action>
                </v-list-item>

                <v-subheader v-if="!mini" class="mt-2">{{ $t('seiten') }}</v-subheader>

                <!-- Magazin -->
                <v-list-item
                    v-if="userIs(['admin', 'editor', 'writer', 'manager'])"
                    link
                    :to="{ name: 'magazin' }"
                    class="nav-entry"
                >
                    <KpIcon size="20" icon-cls="magazine" color="grey" active-color="white" left />
                    <v-list-item-content>
                        <v-list-item-title>{{ $t('magazin') }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                    </v-list-item-action>
                </v-list-item>

                <!-- Agenda -->
                <!-- 2 different main views: standard and compact -->
                <!-- compact view is only visible for staff members when activated in config -->
                <v-list-item
                    v-if="!userIsStaff() || eventView === 'standard'"
                    link
                    :to="{ name: 'event' }"
                    class="nav-entry"
                >
                    <KpIcon size="20" icon-cls="agenda" color="grey" active-color="white" left />
                    <v-list-item-content>
                        <v-list-item-title>{{ $t('agenda') }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                    </v-list-item-action>
                </v-list-item>

                <v-list-item
                    v-if="(userIsStaff() && eventView === 'compact') || (userIsStaff() && multiAgenda)"
                    link
                    :to="{ name: 'event-compact' }"
                    class="nav-entry"
                >
                    <KpIcon size="20" icon-cls="agenda" color="grey" active-color="white" left />
                    <v-list-item-content>
                        <v-list-item-title>{{ $t('agenda_compact') }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                    </v-list-item-action>
                </v-list-item>

                <!-- Adress-Menu -->
                <v-list-group
                    v-model="addressMenuOpen"
                    group="/address/"
                    prepend-icon="ic-20 ic-world ic-col-grey"
                    class="nav-entry"
                    @click="onAddrMenuClick(addressMenuOpen)"
                >
                    <template #activator>
                        <v-list-item-content>
                            <v-list-item-title>{{ $t('adressen') }}</v-list-item-title>
                        </v-list-item-content>
                    </template>

                    <!-- Personen -->
                    <v-list-item link exact :to="{ name: 'address-all' }" class="nav-entry">
                        <v-list-item-content>
                            <v-list-item-title class="pl-1">{{ $t('alle') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <!-- Veranstalter:in - Ausblenden bei Veranstalter ohne Kulturverbund -->
                    <v-list-item
                        v-if="!userIsSingleOrganizer()"
                        link
                        :to="{ name: 'event_organizer-list' }"
                        class="nav-entry"
                    >
                        <v-list-item-content>
                            <v-list-item-title class="pl-1">{{ $t('event_organizers') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <!-- Stammdaten - Nur einblenden bei Veranstalter ohne Kulturverbund -->
                    <v-list-item
                        v-else
                        link
                        :to="{ name: 'address-edit', params: { id: userInfo().event_organizer?.address_id || -1 } }"
                        class="nav-entry"
                    >
                        <v-list-item-content>
                            <v-list-item-title class="pl-1">{{ $t('my_stammdaten') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <!-- Veranstaltungsort -->
                    <v-list-item link :to="{ name: 'locations-list' }" class="nav-entry">
                        <v-list-item-content>
                            <v-list-item-title class="pl-1">{{ $t('locations') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <!-- Personen -->
                    <v-list-item link exact :to="{ name: 'address-people' }" class="nav-entry">
                        <v-list-item-content>
                            <v-list-item-title class="pl-1">{{ $t('people') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <!-- Institutionen -->
                    <v-list-item
                        v-if="userIsStaff()"
                        link
                        exact
                        :to="{ name: 'address-organizations' }"
                        class="nav-entry"
                    >
                        <v-list-item-content>
                            <v-list-item-title class="pl-1">{{ $t('organizations') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <!-- Kulturplatz -->
                    <v-list-item
                        v-if="kulturplatzModuleEnabled()"
                        link
                        :to="{ name: 'address-kulturplatz' }"
                        class="nav-entry mb-2"
                    >
                        <v-list-item-content>
                            <v-list-item-title class="pl-1">{{ $t('kulturplatz') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>
                </v-list-group>

                <v-list-group
                    v-if="userIs(['admin', 'editor', 'manager'])"
                    group="/service"
                    prepend-icon="ic-20 ic-service ic-col-grey"
                    class="nav-entry"
                >
                    <template #activator>
                        <v-list-item-content>
                            <v-list-item-title>{{ $t('service') }}</v-list-item-title>
                        </v-list-item-content>
                    </template>

                    <!-- Service-Pages -->
                    <v-list-item
                        v-if="userIs(['admin', 'editor', 'manager'])"
                        link
                        :to="{ name: 'service' }"
                        class="nav-entry"
                    >
                        <KpIcon size="20" icon-cls="service" color="grey" active-color="white" left />
                        <v-list-item-content>
                            <v-list-item-title>{{ $t('service_pages') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>
                </v-list-group>

                <!-- ------------------------------------------- -->
                <v-subheader v-if="!mini" class="mt-2">{{ $t('medien') }}</v-subheader>
                <v-list-item link :to="{ name: 'media_manager' }" class="nav-entry">
                    <KpIcon size="20" icon-cls="mediamanager" color="grey" active-color="white" left />
                    <v-list-item-content>
                        <v-list-item-title>{{ $t('media_manager') }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                    </v-list-item-action>
                </v-list-item>

                <!-- ------------------------------------------- -->
                <div v-if="userIs(['admin', 'manager'])">
                    <v-subheader v-if="!mini" class="mt-2">{{ $t('admin') }}</v-subheader>

                    <v-list-item link :to="{ name: 'user' }" class="nav-entry">
                        <KpIcon size="20" icon-cls="userin" color="grey" active-color="white" left />
                        <v-list-item-content>
                            <v-list-item-title>{{ $t('benutzer') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-item link :to="{ name: 'requested_user' }" class="nav-entry">
                        <KpIcon size="20" icon-cls="userin" color="grey" active-color="white" left />
                        <v-list-item-content>
                            <v-list-item-title>{{ $t('requested_user') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-item v-if="userIs(['admin'])" class="nav-entry" link :to="{ name: 'banner' }">
                        <KpIcon size="20" icon-cls="info" color="grey" active-color="white" left />
                        <v-list-item-content>
                            <v-list-item-title>{{ $t('banner') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-item v-if="userIs(['admin'])" class="nav-entry" link :to="{ name: 'occupancy_overview' }">
                        <KpIcon size="20" icon-cls="info" color="grey" active-color="white" left />
                        <v-list-item-content>
                            <v-list-item-title>{{ $t('occupancy_overview') }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-group
                        v-if="userIs(['admin', 'manager'])"
                        group="/wertelisten"
                        prepend-icon="ic-20 ic-list ic-col-grey"
                        class="nav-entry"
                    >
                        <template #activator>
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('wertelisten') }}</v-list-item-title>
                            </v-list-item-content>
                        </template>
                        <v-list-item link :to="{ name: 'wl_category' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('kategorien') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_dossier' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('dossier') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_eventfrog_rubric' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('eventfrog_rubriken') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_genre_group' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('rubriken') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_organizer_group' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('kulturverbund') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_mailing_list' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('mailing_listen') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_zip' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('orte') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_region' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('regionen') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_tag_group' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('tag_groups') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_tag' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('tags') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_schedule_status' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('schedule_status') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_weblink' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('weblinks') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                        <v-list-item link :to="{ name: 'wl_target_audience' }" class="nav-entry">
                            <v-list-item-content>
                                <v-list-item-title>{{ $t('target_audience') }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <KpIcon icon-cls="arrowup ic-rotate-90" color="grey" active-color="white" />
                            </v-list-item-action>
                        </v-list-item>
                    </v-list-group>
                </div>

                <v-list-item v-if="userIsStaff()" class="nav-entry" :to="{ name: 'address_duplicates' }">
                    <KpIcon size="20" icon-cls="copy" color="grey" active-color="white" left />
                    <v-list-item-content>
                        <v-list-item-title>{{ $t('addr_dups') }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="userIsStaff()" class="nav-entry" :to="{ name: 'event_duplicates' }">
                    <KpIcon size="20" icon-cls="copy" color="grey" active-color="white" left />
                    <v-list-item-content>
                        <v-list-item-title>{{ $t('event_dups') }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="userIs(['admin'])" class="nav-entry" @click="onClearCacheClick">
                    <KpIcon size="20" icon-cls="clearcache" color="grey" active-color="white" left />
                    <v-list-item-content>
                        <v-list-item-title>{{ $t('clear_cache') }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <!-- ---------- Dynamic Web Link -------------------- -->
                <div v-if="filteredWeblinks?.length > 0">
                    <v-subheader v-if="!mini" class="mt-2">{{ $t('service') }}</v-subheader>

                    <v-list-item
                        v-for="weblink in filteredWeblinks"
                        :key="weblink.id"
                        link
                        :href="weblink.href"
                        target="_blank"
                        class="nav-entry"
                    >
                        <KpIcon size="20" :icon-cls="weblink.icon" color="grey" active-color="white" left />
                        <v-list-item-content>
                            <v-list-item-title>{{ weblink.title }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </div>

                <div v-if="dev">
                    <v-subheader v-if="!mini" class="mt-2">DEV</v-subheader>
                    <v-list-item link :to="{ name: 'cmp-showcase' }" class="nav-entry">
                        <v-list-item-content>
                            <v-list-item-title>Component Showcase</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </div>
            </v-list>
        </v-navigation-drawer>
        <confirm-dialog
            v-model="clearCacheDlg"
            :title="$t('clear_cache')"
            :message="clearCacheMessage"
            @confirm="onClearCacheConfirm"
        />
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapActions } from 'vuex';
import { mapState } from 'vuex';
import MinasaImg from '@/assets/minasa_logo.svg';
import { frontendUrl, globalVar, kulturplatzModuleEnabled, globalParam, userInfo } from '@/helpers/globals';
import { logo, mainNavDrawerCollapseIconColor } from '@/customer-specific';
import { userIs, userIsSingleOrganizer, userIsStaff } from '@/helpers/auth';
import { instance as getApi } from '@/helpers/api';
import { Weblink } from '@/models/Weblink';

export default Vue.extend({
    name: 'MainNavDrawer',
    data: () => ({
        mini: false,
        MinasaImg,
        logo: logo(),
        dev: (globalVar('env') || '').toLowerCase() === 'dev',
        clearCacheDlg: false,
        clearCacheMessage: '',
        addressMenuOpen: false,
        mainNavDrawerCollapseIconColor: mainNavDrawerCollapseIconColor(),
    }),

    computed: {
        ...mapState('user', {
            user: 'record',
        }),
        ...mapState('weblink', {
            weblinks: 'collection',
        }),

        filteredWeblinks(): Weblink[] {
            return this.weblinks?.models.filter((wl: Weblink) => userIs(wl.roles)) || [];
        },
        frontendUrl() {
            return frontendUrl('/');
        },
        eventView() {
            return globalParam('event.main_view') || 'standard';
        },
        multiAgenda() {
            return globalParam('event.multi_agenda');
        },
    },
    mounted() {
        this.loadWeblinks();
    },

    methods: {
        ...mapActions('user', ['logout']),
        ...mapActions(['runAsync']),
        ...mapActions('weblink', {
            loadWeblinks: 'load',
        }),
        userIs,
        userInfo,
        userIsStaff,
        userIsSingleOrganizer,
        kulturplatzModuleEnabled,

        async onClearCacheClick() {
            try {
                this.clearCacheDlg = false;
                const response = await this.runAsync(async () => await getApi().get(`/KP/lastClear`));
                this.clearCacheMessage = String(this.$t('clear_cache_confirm', { clear_date: response.data.data }));
                this.clearCacheDlg = true;
            } catch (error) {
                this.$toast({ message: String(this.$t('clear_cache_failure')), color: 'error' });
            }
        },

        async onClearCacheConfirm() {
            try {
                await this.runAsync(async () => await getApi().get(`/KP/clearCaches`));
                this.$toast({ message: String(this.$t('clear_cache_success')), color: 'success' });
            } catch (error) {
                this.$toast({ message: String(this.$t('clear_cache_failure')), color: 'error' });
            }
        },

        /**
         * Called when the "Kulturplatz" Menu item group is clicked: If it was closed before
         * (the click event comes BEFORE the state change happens), then we navigate to the
         * first menu item.
         */
        onAddrMenuClick(wasOpen: boolean) {
            if (!wasOpen) {
                if (this.$route.name !== 'address-all') {
                    this.$router.push({ name: 'address-all' });
                }
            }
        },
    },
});
</script>

<style lang="scss" scoped>
.customer-saiten {
    .logo {
        margin: 0;
        max-height: 45px;
    }
}
.navdrawer {
    .navheader {
        background-color: var(--navheader-bg-color);
    }
    .v-list-item {
        min-height: 30px;
        height: 30px;
        margin-left: 4px;

        .v-list-item__content {
            padding: 0 !important;
        }
    }

    .v-list-item--active {
        background-color: var(--v-primary-base);
        color: white;
    }

    .v-list-item__icon {
        margin-right: 0 !important;
    }

    .nav-entry {
        margin-bottom: 4px;

        &.v-list-item--active {
            &::before {
                opacity: 0 !important;
            }
            .v-list-item__title,
            .v-list-item__icon {
                color: white !important;
            }
        }

        .v-list-item__title,
        .v-list-item__icon {
            // font-size: 0.825 !important;
            // line-height: 22px !important;
            color: var(--gray-text-color) !important;
        }

        ::v-deep {
            .v-list-group__header {
                height: 30px;
                min-height: 30px;
                margin-bottom: 0;
            }

            .v-list-group__header__prepend-icon {
                margin-top: 4px;
                margin-bottom: 0;
                margin-right: 0 !important;
            }

            .v-list-item__title {
                font-size: 1rem !important;
                font-weight: normal !important;
                letter-spacing: 0;
            }

            .v-list-item__action {
                min-width: 20px;
            }

            .v-list-group__items > .v-list-item {
                padding-left: 36px !important;
            }
        }
    }
}
</style>
